import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Helper to convert YouTube URLs to embed format with autoplay
const getEmbedUrl = (url, autoplay = true) => {
  let videoId = '';
  
  if (url.includes('youtube.com/watch')) {
    videoId = url.split('v=')[1]?.split('&')[0];
  } else if (url.includes('youtube.com/shorts')) {
    videoId = url.split('shorts/')[1]?.split('?')[0];
  } else if (url.includes('youtu.be')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  }
  
  if (!videoId) return url;
  
  const params = autoplay ? '?autoplay=1&rel=0' : '?rel=0';
  return `https://www.youtube.com/embed/${videoId}${params}`;
};

export const VideoModal = ({ isOpen, onClose, videoUrl }) => {
  const iframeRef = useRef(null);
  
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  
  // Stop video when closing
  const handleClose = () => {
    if (iframeRef.current) {
      // Reset iframe src to stop video
      iframeRef.current.src = '';
    }
    onClose();
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          onClick={handleClose}
          data-testid="video-modal-overlay"
        >
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/90" />
          
          {/* Modal content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="relative z-10 w-full max-w-5xl mx-4 sm:mx-6 lg:mx-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="video-modal-content"
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute -top-12 right-0 sm:-right-12 sm:top-0 p-2 text-white/70 hover:text-white transition-colors duration-200 z-20"
              aria-label="Close video"
              data-testid="video-modal-close"
            >
              <X className="w-8 h-8" />
            </button>
            
            {/* Video container with 16:9 aspect ratio */}
            <div className="relative aspect-video bg-black rounded-sm overflow-hidden shadow-2xl">
              <iframe
                ref={iframeRef}
                src={getEmbedUrl(videoUrl, true)}
                title="Video"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="video-modal-iframe"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
